import React, { useState, useEffect, useRef } from 'react';
import { Formik } from 'formik';
import { connect } from 'react-redux';
import { Header, Segment } from 'semantic-ui-react';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';

import { Mode, entityType } from '../../constants';
import { Loading } from 'react-lib/dist/components-es';
import { genericDetailsActions } from '../../behavior/genericDetails/index';
import { changesNotificationModalActions } from '../../behavior/changesNotificationModal';
import DetailsFormOptions from './DetailsFormOptions';
import { initSelectors } from '../../behavior/init';
import { history } from '../../store';
import ChangesNotificationModal from '../Inventory/InventoryDetails/ChangesNotificationModal';
import moment from 'moment';
import useUser from '@behavior/init/getUserV2';

let buttons;
let errors;

const GenericDetails = ({ ...props }) => {
  const [validateOnChange, setValidateOnChange] = useState(props.validateOnChange);

  const prevProps = useRef(props);

  useEffect(() => {
    props.clearMarkAsChecked();
  }, [props.id, props.mode, props.entityTypeProp]);

  useEffect(() => {
    if (
      prevProps.current.id !== props.id ||
      prevProps.current.mode !== props.mode ||
      prevProps.current.updateUser !== props.updateUser ||
      prevProps.current.currentStep !== props.currentStep
    ) {
      buttons = errors = null;
    }

    if (prevProps.current.toggleValidationMessage !== props.toggleValidationMessage) {
      setTimeout(() => {
        if (errors && Object.entries(errors).length > 0) {
          let errorsCounter = 0;
          // first step of CREATE inventory (chemical product selection)
          if (props.currentStep === 1) {
            // a chem prod selection has been made
            if (errors.chemicalProduct && !errors.chemicalProduct.selectedChemicalProduct) {
              errorsCounter += Object.entries(errors.chemicalProduct).length;
              // no chem prod selection
            } else if (props.currentStep === 1 && errors.chemicalProduct) errorsCounter += 1;
            // other than the first step of CREATE inventory (chemical product selection)
          } else {
            errorsCounter += Object.entries(errors).length;
            // a chem prod selection has been made
            if (errors.chemicalProduct && !errors.chemicalProduct.selectedChemicalProduct)
              errorsCounter += Object.entries(errors.chemicalProduct).length - 1;
          }
          props.addValidationMessageToast(errorsCounter);
        }
      }, 500);
    }
    prevProps.current = props;
  }, [props.id, props.mode, props.updateUser, props.currentStep, props.toggleValidationMessage]);

  useEffect(() => {
    return () => {
      props.resetDetails();
      buttons = errors = null;
    };
  }, []);

  const isEditable = () => {
    return props.mode !== Mode.VIEW; //TODO: Implement roles etc.
  };

  const showDetails = () => {
    const { initialValues, initialData } = props;
    return initialValues && Object.keys(initialData).length > 0 && showDetails ? true : false;
  };

  const getButtons = formikProps => {
    const {
      mode,
      id,
      deleteHandler,
      initialValues,
      entityTypeProp,
      initialData,
      currentStep,
      validateAndSetCurrentStep,
    } = props;
    return (
      <DetailsFormOptions
        submitForm={formikProps.submitForm}
        setValidateOnChange={setValidateOnChange}
        values={formikProps.values}
        mode={mode}
        id={id}
        deleteHandler={deleteHandler}
        initialValues={initialValues}
        entityTypeProp={entityTypeProp}
        currentInventoryOrgUnit={initialData.currentInventoryOrgUnit}
        isCurrentlyDeleted={initialData.isCurrentlyDeleted}
        currentStep={currentStep}
        validateAndSetCurrentStep={selectedStep =>
          validateAndSetCurrentStep(selectedStep, formikProps.validateForm)
        }
      />
    );
  };

  const redirectIfNotEditAccess = () => {
    const { mode, initialValues, entityTypeProp } = props;
    const { user } = useUser();
    const department = initialValues?.orgUnit?.name.split('-')[0];

    const isInventoryAndCanNotModifyAsCreator =
      initialValues &&
      entityTypeProp === entityType.inventory &&
      (mode === Mode.EDIT || mode === Mode.UNDO_DELETE) &&
      !initSelectors.isCanModifyAsCreator(
        entityTypeProp,
        user,
        initialValues.contacts ? initialValues.contacts.map(p => p.person.id) : [],
        initialValues.createUser.id,
        department
      );

    const isProductAndCanNotModifyAsCreator =
      initialValues &&
      entityTypeProp === entityType.product &&
      (mode === Mode.EDIT || mode === Mode.UNDO_DELETE) &&
      !initSelectors.isCanModifyAsCreator(
        entityTypeProp,
        user,
        initialValues.contacts ? initialValues.contacts.map(p => p.person.id) : [],
        initialValues.chemicalProduct.createUser.id
      );

    if (
      initialValues &&
      (isProductAndCanNotModifyAsCreator ||
        isInventoryAndCanNotModifyAsCreator ||
        (mode === Mode.CREATE &&
          !initSelectors.isInventoryOrChemProductCanCreate(user, entityTypeProp)))
    ) {
      history.replace('/forbidden');
      history.go(0);
    }
  };

  const getCustomFormikProps = formikProps => {
    const { values, errors, validateOnChange, setFieldValue, setValues } = formikProps;
    return { values, errors, validateOnChange, setFieldValue, setValues };
  };

  const getGenericDetailsClassNames = () => {
    let classes = 'genericDetails';
    const { entityTypeProp, initialValues } = props;

    if (entityTypeProp === entityType.productHist || entityTypeProp === entityType.inventoryHist) {
      if (initialValues.delDte && moment(initialValues.delDte).isBefore(moment.now()))
        classes += ' deletedDetails';
      else classes += ' historyDetails';
    }
    return classes;
  };

  const getFormikRenderContainer = formikProps => {
    const {
      initialData,
      mode,
      validationSchema,
      renderContainer,
      isHistory,
      values,
      validateAndSetCurrentStep,
      currentStep,
    } = props;
    const { requiredFields } = formikProps;

    const detailContainerProps = {
      validationSchema,
      validateOnChange,
      setValidateOnChange,
      initialData,
      disabled: !isEditable(),
      mode,
      isHistory,
      requiredFields,
      entityTypeProp: props.entityTypeProp,
      currentStep,
      validateAndSetCurrentStep: selectedStep =>
        validateAndSetCurrentStep(selectedStep, formikProps.validateForm),
    };

    if (!buttons || JSON.stringify(initialData) !== JSON.stringify(values)) {
      buttons = getButtons(formikProps);
      if (!buttons) setTimeout(() => (currentStep === 1 ? formikProps.validateForm() : null), 0);
    }
    errors = formikProps.errors;

    return (
      <>
        <ChangesNotificationModal
          key="changesNotificationModal"
          inventory={formikProps.values}
          mode={mode}
          submitForm={() => {
            setValidateOnChange(true);
            formikProps.submitForm();
          }}
          externalTrigger={true}
        />
        {renderContainer({
          ...getCustomFormikProps(formikProps),
          ...detailContainerProps,
          buttons,
          formikProps,
        })}
      </>
    );
  };

  const { t, validationSchema, initialValues, title, submitHandle, isLoading } = props;

  const showDetailsVal = showDetails();

  redirectIfNotEditAccess();

  return (
    <>
      <Loading isLoading={isLoading || !showDetailsVal} />
      {showDetailsVal && (
        <Segment className={getGenericDetailsClassNames()}>
          {title && <Header as="h2">{t(title)}</Header>}
          <Formik
            initialValues={initialValues}
            enableReinitialize
            validationSchema={validationSchema}
            validateOnChange={validateOnChange}
            onSubmit={submitHandle}
            render={getFormikRenderContainer}
          />
        </Segment>
      )}
    </>
  );
};
GenericDetails.defaultProps = {
  validateOnChange: false,
};
const mapStateToProps = state => ({
  processedNotificationBody: state.changesNotificationModal.processedChangesNotification,
  recipients: state.changesNotificationModal.recipients,
  markAsChecked: state.changesNotificationModal.markAsChecked,
  toggleValidationMessage: state.genericDetails.toggleValidationMessage,
});
const mapDispatchToProps = dispatch => ({
  clearMarkAsChecked: () => dispatch(changesNotificationModalActions.setMarkAsChecked(false)),
  resetDetails: () => dispatch(genericDetailsActions.resetDetails()),
  addValidationMessageToast: errorsCounter =>
    dispatch(genericDetailsActions.addValidationMessageToast(errorsCounter)),
});
export default compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(GenericDetails);
